import { Content } from "@iventis/translations/content/typed-content";
import { Checkbox, Dialog, DialogContent, TextField } from "@mui/material";
import React, { FunctionComponent, useContext, useState } from "react";
import { Theme } from "@emotion/react";
import { styled } from "@iventis/styles";
import { SavedMapView } from "@iventis/domain-model/model/savedMapView";
import { unwrapResult } from "@reduxjs/toolkit";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, Stages } from "@iventis/components";
import { SavedMapViewStages } from "@iventis/map-engine/src/state/map.state.types";
import { EMPTY_GUID } from "@iventis/utilities";
import { dispatch, RootState, rootStore } from "./state/root.store";
import { createSavedMapView, patchDefaultSavedMapView, updateSavedMapView } from "./state/map.slice.thunks";
import { map } from "./state/map.slice";
import { MapTourContext, MapTourStep } from "./map-tour";

interface MapViewsProps {
    initialNameValue?: string;
    initialDefaultValue?: boolean;
    mapViewId: string;
    cancel: () => void;
}

export const MapViewsForm: FunctionComponent<MapViewsProps> = ({ initialNameValue = "", initialDefaultValue = false, mapViewId, cancel }) => {
    const [nameValue, setValue] = useState(initialNameValue);
    const [defaultValue, setDefaultValue] = useState(initialDefaultValue);
    const [submitting, setSubmitting] = useState(false);
    const translate = useIventisTranslate();
    const header = translate(Content.map.saved_map_view.form_header);
    const checkboxLabel = translate(Content.map.saved_map_view.default_label);
    const checkBoxDescription = translate(Content.map.saved_map_view.default_description);
    const nameLabel = translate(Content.map.saved_map_view.name_label);

    const { ref: mapTourRef } = useContext(MapTourContext);

    const Stages: Stages = [
        {
            primaryButtonText: translate(Content.common.buttons.save),
            primaryButtonCallback: () => {
                submitMapViewValues(mapViewId, nameValue, defaultValue);
            },
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: cancel }],
            isValid: nameValue.length > 0,
        },
    ];

    const submitMapViewValues = async (id: string, name: string, viewDefault: boolean) => {
        try {
            setSubmitting(true);
            const { mapReducer } = rootStore.getState() as RootState;
            const { position, currentLevel } = mapReducer.mapModule;
            const { savedMapViewDefault } = mapReducer;
            const { mapId } = mapReducer.mapModule;
            const mapView: SavedMapView = {
                id,
                name,
                shortName: "",
                position: {
                    latitude: position.value.lat,
                    longitude: position.value.lng,
                    zoom: position.value.zoom,
                    bearing: position.value.bearing,
                    pitch: position.value.pitch,
                },
                mapId,
                projectId: EMPTY_GUID,
                level: currentLevel,
            };

            // If map view Id exists, assume updating and not creating
            if (mapViewId) {
                unwrapResult(await rootStore.dispatch(updateSavedMapView(mapView)));
            } else {
                const payload = unwrapResult(await rootStore.dispatch(createSavedMapView(mapView)));
                mapView.id = payload.id;
            }
            // Update the default map view if true and is not already the default
            if (viewDefault && savedMapViewDefault !== mapView.id) {
                // Update map
                await dispatch(patchDefaultSavedMapView({ mapId, savedMapViewId: mapView.id }));
                // Or remove view default if it currently is the default.
            } else if (!viewDefault && savedMapViewDefault !== null && savedMapViewDefault === mapView.id) {
                await dispatch(patchDefaultSavedMapView({ mapId, savedMapViewId: null }));
            }
            // Clean up saved view wizard
            dispatch(map.actions.setSavedViewMapWizard({ stage: SavedMapViewStages.NONE, id: undefined }));
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(`Unable to save map view - ${e}`);
        } finally {
            setSubmitting(false);
            // Once we have saved our map view and we have the tour running on the background step, reopen it
            if (
                mapTourRef?.current &&
                mapTourRef.current.isRunning() &&
                (mapTourRef.current.getStep() === MapTourStep.LOCATION || mapTourRef.current.getStep() === MapTourStep.BACKGROUND)
            ) {
                mapTourRef.current.maximiseTour();

                // We may have already progressed to the next step depending if we clicked through the spotlight or not
                if (mapTourRef.current.getStep() === MapTourStep.LOCATION) mapTourRef.current.nextStep();
            }
        }
    };

    return (
        <Dialog open maxWidth="lg">
            <FormWizardTemplate title={header} stages={Stages} currentStage={0} isSubmitting={submitting}>
                <DialogContent style={{ marginTop: "10px", marginBottom: "40px" }} data-testid="saved-map-view-form">
                    <div>{nameLabel}</div>
                    <TextField
                        style={{ width: "100%", marginTop: "10px" }}
                        value={nameValue}
                        onChange={(event) => setValue(event.target.value)}
                        data-testid="map-saved-view-name-input"
                    />
                    <CheckBoxContainer>
                        <Checkbox style={{ marginLeft: "-9px" }} checked={defaultValue} onClick={() => setDefaultValue(!defaultValue)} />

                        <div>{checkboxLabel}</div>
                        <CheckBoxDescription>{checkBoxDescription}</CheckBoxDescription>
                    </CheckBoxContainer>
                </DialogContent>
            </FormWizardTemplate>
        </Dialog>
    );
};

const CheckBoxContainer = styled.div`
    display: grid;
    grid-template-columns: auto 2fr;
    grid-template-row: repeat(2, 1fr);
    align-items: center;
    margin-top: 10px;
`;

const CheckBoxDescription = styled.div`
    grid-column: 2;
    grid-row: 2;
    color: ${({ theme }: { theme: Theme }) => theme.shades.two};
`;
